import moment from 'moment';
import { formatMoneyAddDecimalPointAndThousands } from '../../util/PaymentUtils';
import type { KnowledgeBreakdownRow } from './knowledgeRegistry';

/**
 * Small formatting helpers shared by the record lookups (invoice, booking,
 * equipment, account), which render a row the backend sent rather than a count.
 *
 * The backend is not consistent about dates — invoice and booking rows send
 * `MM/dd/yyyy`, audit timestamps ISO-8601, gate activity `MM/dd/yyyy hh:mm a` in
 * the location's own time zone — so {@link displayDate} passes a slash date
 * through untouched and only parses what is unambiguously ISO. Re-parsing a
 * location-local time in the browser's zone would move it.
 */

const SLASH_DATE = /^\d{2}\/\d{2}\/\d{4}\b/;

/** A backend date as `MM/DD/YYYY`, or '' when there is none. */
export const displayDate = (value?: string | null): string => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '';
  const slash = trimmed.match(SLASH_DATE);
  if (slash) return slash[0];
  const parsed = moment(trimmed, moment.ISO_8601, true);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : trimmed;
};

/** "02/03/2026 – 03/02/2026", one side alone, or '' when neither is known. */
export const displayDateRange = (start?: string | null, end?: string | null): string => {
  const from = displayDate(start);
  const to = displayDate(end);
  if (from && to) return `${from} \u2013 ${to}`;
  return from || to;
};

/**
 * The day of `value` moved by `days`, as `MM/DD/YYYY`, or '' when it has no day.
 *
 * A link that filters a report to a record's date widens the window by a day:
 * the day a row shows and the day the report filters on can be taken in
 * different time zones, so the record's own day can fall just outside it.
 */
export const shiftedDay = (value: string | null | undefined, days: number): string => {
  const day = moment(displayDate(value), 'MM/DD/YYYY', true);
  return day.isValid() ? day.add(days, 'days').format('MM/DD/YYYY') : '';
};

/** An amount in cents as dollars, or '' when the row has no such amount. */
export const money = (cents?: number | null): string =>
  typeof cents === 'number' && Number.isFinite(cents)
    ? formatMoneyAddDecimalPointAndThousands(cents)
    : '';

/** `SUPPLIER_PAYOUT_PENDING` → "Supplier Payout Pending", for enums with no label map. */
export const enumLabel = (value?: string | null): string =>
  (value ?? '')
    .trim()
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/** "Jane Smith", either half alone, or ''. */
export const personName = (first?: string | null, last?: string | null): string =>
  [first, last]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');

/**
 * Breakdown rows for the pairs that have a value. A lookup lists whatever the
 * record carries; a blank line for every field it lacks would bury the rest.
 */
export const breakdownRows = (
  pairs: Array<[label: string, value: string | number | null | undefined]>,
): KnowledgeBreakdownRow[] =>
  pairs
    .map(([label, value]) => ({ label, value: value === null || value === undefined ? '' : String(value).trim() }))
    .filter((row) => row.value !== '');

/** True when a request failed because the signed-in user's role may not read it. */
export const isForbidden = (err: unknown): boolean => {
  const status =
    (err as { status?: number } | null)?.status ??
    (err as { response?: { status?: number } } | null)?.response?.status;
  return status === 403;
};
